/**
 * 便利ツール共通の共有・コピーユーティリティ
 */
import toast from 'react-hot-toast';

import { generateShareImage, type ShareImageOptions } from '@/utils/shareImage';

/** ツール共有時のフッターテキスト */
export function toolFooter(toolName: string): string {
  return `\n---\n企画苔 - 便利ツール - ${toolName}`;
}

/** Web Share API でURLを共有。非対応/キャンセル時はクリップボードにコピー */
export async function shareUrl(options: {
  title: string;
  url: string;
  text?: string;
}): Promise<void> {
  if (navigator.share) {
    try {
      await navigator.share(options);
      return;
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
    }
  }
  try {
    await navigator.clipboard.writeText(options.url);
    toast.success('URLをコピーしました');
  } catch {
    toast.error('コピーに失敗しました');
  }
}

/** ゲーム用シェアテキスト（URL・ハッシュタグ付き） */
function gameShareText(toolName: string, scoreText: string, url: string): string {
  return `${scoreText}${toolFooter(toolName)}\n${url}\n\n#企画苔 #${toolName}`;
}

/** ゲームスコアをシェア（テキスト + ゲームURL） */
export async function shareGameScore(toolName: string, scoreText: string): Promise<void> {
  const url = `${window.location.origin}${window.location.pathname}`;
  await shareUrl({ title: toolName, url, text: gameShareText(toolName, scoreText, url) });
}

/** ゲームスコアを画像付きでシェア。非対応時はテキストのみにフォールバック */
export async function shareGameScoreWithImage(
  toolName: string,
  scoreText: string,
  imageOptions: Omit<ShareImageOptions, 'toolName'>,
): Promise<void> {
  const url = `${window.location.origin}${window.location.pathname}`;
  const text = gameShareText(toolName, scoreText, url);

  try {
    const blob = await generateShareImage({ toolName, ...imageOptions });
    const file = new File([blob], `${toolName}.png`, { type: 'image/png' });
    const shareData: ShareData = { title: toolName, text, url, files: [file] };

    if (navigator.canShare?.(shareData)) {
      await navigator.share(shareData);
      return;
    }
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') return;
    // fall through to text-only share
  }

  // Fallback: text-only share
  await shareUrl({ title: toolName, url, text });
}

/** テキストをクリップボードにコピー */
export function copyText(text: string): void {
  navigator.clipboard.writeText(text).then(
    () => toast.success('テキストをコピーしました'),
    () => toast.error('コピーに失敗しました'),
  );
}
